import React, { useCallback, useState } from 'react'
import { View, StyleSheet, ActivityIndicator, Pressable } from 'react-native'
import { useRouter } from 'expo-router'
import { useMutation } from 'convex/react'
import { api } from '@ppwr/convex/_generated/api'
import type { Id } from '@ppwr/convex/_generated/dataModel'
import { LOUNGE_INVITE_ONLY_ERROR_CODE } from '@ppwr/convex/lounges/errors'
import { LOUNGE_FULL_ERROR_CODE } from '@ppwr/convex/lounges/capacity'

import { DS } from '@/design-system'
import { useStore } from '@/models'
import { translate } from '../../i18n'
import { isConvexErrorWithCode } from '../../utils/errors/convex'
import { palette } from '../../theme/palette'

interface JoinConfirmModalProps {
  gameId: Id<'games'>
  userId: string
  code: string
}

/**
 * Modal shown when user arrives via deep link to confirm joining the game
 * Uses absolute positioning instead of RN Modal to avoid visibility issues
 */
export const JoinConfirmModal = ({ gameId, userId, code }: JoinConfirmModalProps) => {
  const router = useRouter()
  const { user } = useStore().session
  const [isJoining, setIsJoining] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const joinGameMutation = useMutation(api.games.mutations.joinGame)

  const handleJoin = useCallback(async () => {
    setIsJoining(true)
    setError(null)

    // Validate mongoUserId exists (required for analytics tracking)
    const mongoUserId = user?._id?.toString()
    if (!mongoUserId) {
      setError('User not authenticated. Please log in and try again.')
      setIsJoining(false)
      return
    }

    try {
      await joinGameMutation({
        gameId,
        userId,
        name: user?.name || 'Guest Player',
        mongoUserId
      })

      // Successfully joined - clear the pendingJoin param to dismiss modal
      // We're already on the game screen, no need to navigate
      router.setParams({ pendingJoin: undefined, code: undefined })
    } catch (err) {
      // Map typed Convex errors to friendly copy; never surface the raw Convex wrapper.
      console.error('Failed to join game:', err)
      if (isConvexErrorWithCode(err, LOUNGE_INVITE_ONLY_ERROR_CODE)) {
        setError(translate('joinConfirmModal.inviteOnly'))
      } else if (isConvexErrorWithCode(err, LOUNGE_FULL_ERROR_CODE)) {
        setError(translate('joinConfirmModal.loungeFull'))
      } else {
        setError(translate('joinConfirmModal.genericError'))
      }
      setIsJoining(false)
    }
  }, [gameId, userId, user, joinGameMutation, router])

  const handleCancel = useCallback(() => {
    // Go back to play tab
    router.replace('/(root)/(tabs)/play')
  }, [router])

  return (
    <View style={styles.overlay} pointerEvents="box-none">
      <Pressable style={styles.backdrop} onPress={handleCancel} />
      <View style={styles.modalContainer}>
        <View style={styles.container}>
          <DS.Text variant="Heading 2/Medium" textAlign="center">
            Join Game?
          </DS.Text>

          <DS.Frame alignItems="center" gap="8">
            <DS.Text variant="Caption/Bold" color="Brand/Dark Grey" uppercase>
              Game Code
            </DS.Text>
            <DS.Text variant="Heading 1/Medium" style={styles.codeText}>
              {code.toUpperCase()}
            </DS.Text>
          </DS.Frame>

          <DS.Text variant="Body Small/Regular" textAlign="center" color="Brand/Dark Grey">
            You're about to join this game. Ready to play?
          </DS.Text>

          {error && (
            <DS.Text variant="Body Small/Regular" textAlign="center" color="Brand/Orange">
              {error}
            </DS.Text>
          )}

          <DS.Frame gap="8" justifyContent="center" alignItems="center">
            <DS.Button
              onPress={handleJoin}
              variant="Medium/Primary"
              type="On Light"
              style={styles.button}
              disabled={isJoining}
            >
              {isJoining ? <ActivityIndicator size="small" color={DS.palette['Global/White']} /> : 'Join Game'}
            </DS.Button>
            <DS.Button
              onPress={handleCancel}
              variant="Medium/Secondary"
              type="On Dark"
              style={styles.button}
              disabled={isJoining}
            >
              Cancel
            </DS.Button>
          </DS.Frame>
        </View>
      </View>
    </View>
  )
}

const styles = StyleSheet.create({
  overlay: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 9999,
    justifyContent: 'center',
    alignItems: 'center'
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: palette.blackWithOpacity.twenty
  },
  modalContainer: {
    backgroundColor: DS.palette['Brand/Cream'],
    alignItems: 'center',
    marginHorizontal: 24,
    paddingHorizontal: 24,
    borderRadius: 20,
    maxWidth: 400,
    width: '85%'
  },
  container: {
    width: '100%',
    paddingVertical: 24,
    gap: 20
  },
  codeText: {
    letterSpacing: 4,
    color: DS.palette['Brand/Maroon']
  },
  button: {
    width: '100%'
  }
})
